import { Shell } from '../Shell';
import { useAsync } from '../../core/useAsync';
import { cachedLeague, getMe, getResults, getSeasonRounds } from '../../core/api';
import { avatarStyle, gradFor, initials, ordinal } from '../format';
import type { LeagueInfo, Me, Results, SeasonRound } from '../../core/types';
import s from './Reveal.module.css';

// The payoff: the most recently closed round's results — submitters revealed,
// ranked by score, plus your own round summary. Wired to GET /api/rounds/{id}/results.

const flagClass = (f: string) => (f === 'Vetoed' ? 'veto' : 'you');

export function Reveal() {
  const league = useAsync<LeagueInfo>(cachedLeague, []);
  const seasonId = league.data?.season_id ?? null;
  const rounds = useAsync<SeasonRound[]>(() => (seasonId != null ? getSeasonRounds(seasonId) : Promise.resolve([])), [seasonId]);
  const me = useAsync<Me>(getMe, []);
  const closed = [...(rounds.data ?? [])].filter((r) => r.phase === 'CLOSED' || r.closed_at).sort((a, b) => b.sequence_no - a.sequence_no);
  const targetId = closed[0]?.round_id ?? null;
  const res = useAsync<Results | null>(() => (targetId ? getResults(targetId) : Promise.resolve(null)), [targetId]);

  if (league.loading || rounds.loading || (targetId && res.loading)) {
    return <Shell><div style={{ padding: 48, textAlign: 'center', color: 'var(--mut)' }}>Loading…</div></Shell>;
  }
  if (!res.data) {
    return (
      <Shell>
        <div className={s.appbar}><span className={s.ctx}>{league.data?.name ?? 'Results'}</span></div>
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--mut)' }}>No results yet — they appear once a round closes.</div>
      </Shell>
    );
  }

  const r = res.data;
  const myId = me.data?.user?.user_id ?? null;
  const tracks = [...r.tracks].sort((a, b) => a.placing - b.placing);
  const winner = tracks[0];

  return (
    <Shell>
      <div className={s.appbar}>
        <span className={s.ctx}>{league.data?.name ?? 'Results'}</span>
        <button className={s.menu} aria-label="Menu">⋯</button>
      </div>
      <div className={s.head}>
        <h2>{r.theme}</h2>
        <p>Results · {tracks.length} entries</p>
      </div>

      {/* Desktop: winner + your-round summary sit beside the full result. */}
      <div className={s.split}>
        <div className={s.left}>
          {winner ? (
            <div className={s.winner}>
              <span className={s.cov} style={{ background: gradFor(winner.submission_id) }} />
              <div className={s.wc}>
                <span className={s.wtag}>♛ Winner</span>
                <h3>{winner.title}</h3>
                <div className={s.by}><span className={s.av} style={avatarStyle(winner.submitted_by.avatar_url, winner.submitted_by.user_id)}>{winner.submitted_by.avatar_url ? '' : initials(winner.submitted_by.display_name)}</span>submitted by {winner.submitted_by.display_name}</div>
              </div>
              <div className={s.sc}><b>{winner.final_score}</b><small>pts</small></div>
            </div>
          ) : null}

          <div className={s.mine}>
            <div className={s.cell}><small>Your entry</small><b>{r.you.track_points}</b></div>
            <div className={s.cell}><small>You placed</small><b>{ordinal(r.you.placing)}</b></div>
            <div className={s.cell}><small>Named you</small><b>{r.you.informants}</b></div>
          </div>
        </div>

        <div className={s.right}>
          <div className={s.sec}>Full result</div>
          <div className={s.rows}>
            {tracks.map((tr) => {
              const flags: string[] = [];
              if (tr.placing === 1) flags.push('Winner');
              if (myId != null && tr.submitted_by.user_id === myId) flags.push('You');
              if (tr.veto_applied) flags.push('Vetoed');
              return (
                <div key={tr.submission_id} className={`${s.rrow}${tr.placing === 1 ? ' ' + s.top : ''}`}>
                  <span className={s.pos}>{tr.placing}</span>
                  <span className={s.cov} style={{ background: gradFor(tr.submission_id) }} />
                  <span className={s.info}>
                    <b>{tr.title}</b>
                    <span className={s.who}><span className={s.a2} style={avatarStyle(tr.submitted_by.avatar_url, tr.submitted_by.user_id)}>{tr.submitted_by.avatar_url ? '' : initials(tr.submitted_by.display_name)}</span>{tr.submitted_by.display_name}</span>
                    {flags.length ? <span className={s.flags}>{flags.map((f) => <span key={f} className={`${s.flag} ${s[flagClass(f)]}`}>{f}</span>)}</span> : null}
                  </span>
                  <span className={s.score}>{tr.final_score}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Shell>
  );
}
